/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  TextField,
  Typography,
  IconButton,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { placeholderText } from "../../constants/colors";
import _ from "lodash";

import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

import { ReactComponent as DropdownIcon } from "../../assets/Vector 474 (Stroke).svg";
import CommonSelect from "../common/CommonSelect";
import {
  searchRecipientMembers,
  searchRecipientProviders,
  searchRecipientPharmacies,
  fetchLinkedMembers,
  fetchRecipientPrograms,
  fetchPharmacyDetails,
  fetchProviderDetails,
} from "../../api/manualTask";
import { DateTime } from "../opportunities/OpportunitiesTable";

import { capitalizeFirstLetter } from "../../utils/capitalize";
// Presentational component moved out of modal to avoid redefining on every render
function RecentActivityDisplay({
  recentActivityStatus,
  recentActivityAt,
  recentActivityStatusColor,
}) {
  return (
    <Box
      sx={{
        p: 2,
        bgcolor: "transparent",
        width: "100%",
        "& .MuiGrid-container": {
          width: "100%",
          margin: 0,
        },
      }}
    >
      <Typography
        variant="subtitle1"
        fontWeight="bold"
        mb={1}
        fontSize={16}
        sx={{ textAlign: "left", pl: 1 }}
      >
        Most Recent Activity
      </Typography>
      <Box
        sx={{
          bgcolor: "transparent",
          p: 1,
          width: "30%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography
          sx={{
            display: "inline-block",
            bgcolor: "#F7F8FB",
            color: recentActivityStatusColor || "#6c757d",
            fontSize: 12,
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontWeight: "600",
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {recentActivityStatus}
        </Typography>
        <Typography
          sx={{
            display: "inline-block",
            bgcolor: "#F7F8FB",
            color: "#403C6F",
            fontSize: 12,
            px: 1,
            py: 0.5,
            borderRadius: 1,
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {recentActivityAt ? DateTime(recentActivityAt) : "N/A"}
        </Typography>
      </Box>
    </Box>
  );
}

RecentActivityDisplay.propTypes = {
  recentActivityStatus: PropTypes.string,
  recentActivityStatusColor: PropTypes.string,
  recentActivityAt: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
    PropTypes.number,
  ]),
};

// Small presentational end-adornment moved out to avoid redefining inside modal
function RecipientEndAdornment({
  searchLoading,
  recipientType,
  selectedRecipient,
  onClear,
  onTrigger,
}) {
  if (searchLoading && !!recipientType) {
    return (
      <CircularProgress
        color="inherit"
        size={20}
        sx={{ mr: 1 }}
        aria-label="Searching recipients"
      />
    );
  }
  if (selectedRecipient) {
    return (
      <IconButton
        onClick={onClear}
        aria-label="Clear recipient selection"
        sx={{
          color: "#999893",
          "&:hover": { color: "#403C6F" },
        }}
      >
        <CloseIcon />
      </IconButton>
    );
  }
  if (recipientType !== "others") {
    return (
      <IconButton
        onClick={onTrigger}
        disabled={!recipientType}
        aria-label="Search recipients"
        sx={{
          color: "#999893",
          "&.Mui-disabled": {
            color: "#999893",
          },
          "&:hover": {
            color: recipientType ? "#403C6F" : "#999893",
          },
        }}
      >
        <SearchIcon />
      </IconButton>
    );
  }
  return null;
}

RecipientEndAdornment.propTypes = {
  searchLoading: PropTypes.bool,
  recipientType: PropTypes.string,
  selectedRecipient: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    displayName: PropTypes.string,
  }),
  onClear: PropTypes.func,
  onTrigger: PropTypes.func,
};

RecipientEndAdornment.propTypes = {
  searchLoading: PropTypes.bool,
  recipientType: PropTypes.string,
  selectedRecipient: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    displayName: PropTypes.string,
  }),
  onClear: PropTypes.func,
  onTrigger: PropTypes.func,
};

// Top-level: Recipient type + name
function RecipientTypeAndName({
  formData,
  renderDropdownButton,
  RECIPIENT_TYPES,
  anchorEl,
  filterButtonRefs,
  handleCloseSelect,
  handleTypeToggle,
  renderRecipientSearchInput,
}) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "24px", width: "100%" }}>
      <Box sx={{ width: { xs: "100%", sm: "calc(50% - 12px)" } }}>
        <Typography
          variant="subtitle2"
          color="#403C6F"
          fontWeight={500}
          fontSize={14}
          gutterBottom
        >
          Recipient Type
        </Typography>
        {renderDropdownButton(
          "recipientType",
          formData.recipientType,
          RECIPIENT_TYPES,
          "Select the type of recipient",
        )}
        <CommonSelect
          id="recipient-type-select"
          open={Boolean(anchorEl.recipientType)}
          anchorEl={anchorEl.recipientType}
          onClose={() => handleCloseSelect("recipientType")}
          inQuickview={false}
          filterButtonRef={filterButtonRefs.recipientType}
          noteTypes={RECIPIENT_TYPES.map((t) => t.label)}
          selectedTypes={
            formData.recipientType
              ? [
                  RECIPIENT_TYPES.find(
                    (t) => t.value === formData.recipientType,
                  )?.label,
                ]
              : []
          }
          handleTypeToggle={(label) => {
            const sel = RECIPIENT_TYPES.find((t) => t.label === label);
            if (sel) handleTypeToggle("recipientType", sel.value);
          }}
          multiple={false}
          width={
            filterButtonRefs.recipientType.current
              ? `${filterButtonRefs.recipientType.current.offsetWidth}px`
              : "auto"
          }
        />
      </Box>
      <Box sx={{ width: { xs: "100%", sm: "calc(50% - 12px)" } }}>
        <Typography
          variant="subtitle2"
          color="#403C6F"
          fontWeight={500}
          fontSize={14}
          gutterBottom
        >
          Recipient Name
        </Typography>
        {renderRecipientSearchInput?.()}
      </Box>
    </Box>
  );
}

RecipientTypeAndName.propTypes = {
  formData: PropTypes.object.isRequired,
  renderDropdownButton: PropTypes.func.isRequired,
  RECIPIENT_TYPES: PropTypes.array.isRequired,
  anchorEl: PropTypes.object.isRequired,
  filterButtonRefs: PropTypes.object.isRequired,
  handleCloseSelect: PropTypes.func.isRequired,
  handleTypeToggle: PropTypes.func.isRequired,
  renderRecipientSearchInput: PropTypes.func,
};

// Top-level: Linked member select
function LinkedMemberSelect({
  formData,
  renderDropdownButton,
  linkedMembers,
  errors,
  anchorEl,
  filterButtonRefs,
  handleCloseSelect,
  handleInputChange,
}) {
  return (
    <Box sx={{ width: "100%", display: "flex", flexWrap: "wrap", gap: "24px" }}>
      {(formData.recipientType === "provider" ||
        formData.recipientType === "pharmacy") && (
        <Box sx={{ width: { xs: "100%", sm: "100%" } }}>
          <Typography
            variant="subtitle2"
            color="#403C6F"
            fontWeight={500}
            fontSize={14}
            gutterBottom
          >
            Select Linked Member
            {(formData.recipientType === "provider" ||
              formData.recipientType === "pharmacy") &&
              " "}
          </Typography>
          <Box
            sx={{
              pointerEvents: formData.recipientName ? "auto" : "none",
              "& .MuiButton-root": {
                backgroundColor: formData.recipientName ? "#FFFFFF" : "#EEEEEE",
                "&:hover": {
                  backgroundColor: formData.recipientName
                    ? "#FFFFFF"
                    : "#EEEEEE",
                },
              },
            }}
          >
            {renderDropdownButton(
              "linkedMember",
              formData.linkedMember,
              linkedMembers.map((m) => ({
                value: m.id,
                label: capitalizeFirstLetter(m.name),
              })),
              formData.recipientName
                ? "Select a member"
                : "Select recipient first",
            )}
            {errors.linkedMember && (
              <Typography
                variant="caption"
                sx={{
                  mt: 0.5,
                  ml: 0.5,
                  color: "#d32f2f",
                  fontSize: 12,
                  fontWeight: 500,
                  display: "block",
                }}
              >
                {errors.linkedMember}
              </Typography>
            )}
            <Menu
              id="linked-member-menu"
              anchorEl={anchorEl.linkedMember}
              open={Boolean(anchorEl.linkedMember) && !!formData.recipientName}
              onClose={() => handleCloseSelect("linkedMember")}
              slotProps={{
                paper: {
                  sx: {
                    backgroundColor: "#6E6D92",
                    color: "white",
                    borderRadius: "6px",
                    boxShadow: "0px 4px 8px rgba(0,0,0,0.1)",
                    minWidth: filterButtonRefs.linkedMember?.current
                      ? `${filterButtonRefs.linkedMember.current.offsetWidth}px`
                      : 160,
                    maxHeight: "200px",
                    overflow: "auto",
                  },
                },
                list: {
                  sx: {
                    p: 0,
                    "&::-webkit-scrollbar": { width: "6px" },
                    "&::-webkit-scrollbar-track": {
                      background: "rgba(0, 0, 0, 0.1)",
                      borderRadius: "3px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "rgba(255, 255, 255, 0.3)",
                      borderRadius: "3px",
                      "&:hover": { background: "rgba(255, 255, 255, 0.4)" },
                    },
                  },
                },
              }}
            >
              {linkedMembers.length === 0 && (
                <MenuItem disabled sx={{ opacity: 0.7, color: "white" }}>
                  No members found
                </MenuItem>
              )}
              {linkedMembers.map((m) => (
                <MenuItem
                  key={m.id}
                  onClick={() => {
                    handleInputChange("linkedMember", m.id);
                    handleCloseSelect("linkedMember");
                  }}
                  sx={{
                    backgroundColor:
                      formData.linkedMember === m.id
                        ? "rgba(255,255,255,0.15)"
                        : "transparent",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
                    fontSize: "0.875rem",
                    py: 1,
                    px: 2,
                    color: "white",
                    textShadow: "0 0 1px rgba(0, 0, 0, 0.3)",
                  }}
                >
                  {capitalizeFirstLetter(m.name)}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Box>
      )}
    </Box>
  );
}

LinkedMemberSelect.propTypes = {
  formData: PropTypes.object.isRequired,
  renderDropdownButton: PropTypes.func.isRequired,
  linkedMembers: PropTypes.array,
  errors: PropTypes.object,
  anchorEl: PropTypes.object,
  filterButtonRefs: PropTypes.object,
  handleCloseSelect: PropTypes.func,
  handleInputChange: PropTypes.func.isRequired,
};

// Top-level: Time zone and language
function TimeZoneAndLanguage({
  formData,
  renderDropdownButton,
  TIMEZONE_OPTIONS,
  anchorEl,
  filterButtonRefs,
  handleCloseSelect,
  handleTypeToggle,
  sanitizeLanguage,
  handleInputChange,
}) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "24px", width: "100%" }}>
      <Box sx={{ width: { xs: "100%", sm: "calc(50% - 12px)" } }}>
        <Typography
          variant="subtitle2"
          color="#403C6F"
          fontWeight={500}
          fontSize={14}
          gutterBottom
        >
          Time Zone
        </Typography>
        <Box
          sx={{
            pointerEvents:
              formData.recipientType === "others" ? "auto" : "none",
            "& .MuiButton-root": {
              backgroundColor:
                formData.recipientType === "others" ? "#FFFFFF" : "#EEEEEE",
              color: formData.recipientType === "others" ? "#000" : "#999893",
              cursor:
                formData.recipientType === "others" ? "pointer" : "not-allowed",
              "&:hover": {
                backgroundColor:
                  formData.recipientType === "others" ? "#FFFFFF" : "#EEEEEE",
              },
              "&:focus-visible": {
                outline: "2px solid #403C6F",
                outlineOffset: "2px",
              },
              "& span": { wordBreak: "break-word", overflowWrap: "break-word" },
            },
          }}
        >
          {renderDropdownButton(
            "timeZone",
            formData.timeZone,
            TIMEZONE_OPTIONS,
            "Select Time Zone",
          )}
          {formData.recipientType === "others" && (
            <CommonSelect
              id="timezone-select"
              open={Boolean(anchorEl.timeZone)}
              anchorEl={anchorEl.timeZone}
              onClose={() => handleCloseSelect("timeZone")}
              inQuickview={false}
              filterButtonRef={filterButtonRefs.timeZone}
              noteTypes={TIMEZONE_OPTIONS.map((opt) => opt.label)}
              selectedTypes={
                formData.timeZone
                  ? [
                      TIMEZONE_OPTIONS.find(
                        (t) => t.value === formData.timeZone,
                      )?.label,
                    ]
                  : []
              }
              handleTypeToggle={(type) => {
                const selected = TIMEZONE_OPTIONS.find((t) => t.label === type);
                if (selected) {
                  handleTypeToggle("timeZone", selected.value);
                }
              }}
              multiple={false}
              width={
                filterButtonRefs.timeZone.current
                  ? `${filterButtonRefs.timeZone.current.offsetWidth}px`
                  : "auto"
              }
            />
          )}
        </Box>
      </Box>
      <Box sx={{ width: { xs: "100%", sm: "calc(50% - 12px)" } }}>
        <Typography
          variant="subtitle2"
          color="#403C6F"
          fontWeight={500}
          fontSize={14}
          gutterBottom
        >
          Language{formData.recipientType === "others" && " "}
        </Typography>
        <Box
          sx={{
            pointerEvents:
              formData.recipientType === "others" ? "auto" : "none",
          }}
        >
          <Box sx={{ position: "relative" }}>
            <TextField
              fullWidth
              value={formData.language}
              onChange={(e) => {
                const sanitized = sanitizeLanguage(e.target.value);
                handleInputChange("language", sanitized);
              }}
              placeholder={
                formData.recipientType === "others"
                  ? "Enter language"
                  : "Select Language"
              }
              disabled={formData.recipientType !== "others"}
              size="small"
              slotProps={{
                htmlInput: { maxLength: 15 },
                input: {
                  sx: {
                    height: "40px",
                    "& input": {
                      padding: "8px 14px",
                      color:
                        formData.recipientType === "others"
                          ? "inherit"
                          : "#999999",
                      cursor:
                        formData.recipientType === "others"
                          ? "text"
                          : "not-allowed",
                    },
                  },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor:
                    formData.recipientType === "others" ? "#FFFFFF" : "#EEEEEE",
                  border: "1px solid #E9E9E8",
                  height: "40px !important",
                  borderRadius: "4px",
                  padding: "0px !important",
                  "& fieldset": { borderColor: "#E9E9E8" },
                  "&:hover fieldset": {
                    borderColor:
                      formData.recipientType === "others"
                        ? "#403C6F"
                        : "#E9E9E8",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor:
                      formData.recipientType === "others"
                        ? "#403C6F"
                        : "#E9E9E8",
                  },
                  "&.Mui-disabled": {
                    ...disabledStyles,
                    "& fieldset": { borderColor: "#E9E9E8 !important" },
                  },
                },
                "& input::placeholder": {
                  color:
                    formData.recipientType === "others" ? "#999893" : "#CCCCCC",
                  opacity: 1,
                },
              }}
            />
            {formData.recipientType === "others" && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 4,
                  right: 8,
                  fontSize: "12px",
                  backgroundColor: "rgba(255,255,255,0.9)",
                  padding: "2px 6px",
                  borderRadius: "4px",
                  pointerEvents: "none",
                  color: "#5A5875",
                }}
              >
                {formData.language?.length || 0}/15
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

TimeZoneAndLanguage.propTypes = {
  formData: PropTypes.object.isRequired,
  renderDropdownButton: PropTypes.func.isRequired,
  TIMEZONE_OPTIONS: PropTypes.array.isRequired,
  anchorEl: PropTypes.object,
  filterButtonRefs: PropTypes.object,
  handleCloseSelect: PropTypes.func.isRequired,
  handleTypeToggle: PropTypes.func.isRequired,
  sanitizeLanguage: PropTypes.func.isRequired,
  handleInputChange: PropTypes.func.isRequired,
};

// Top-level: RecipientDetailsSection composing the above
function RecipientDetailsSection(props) {
  return (
    <Box
      sx={{
        p: 2,
        bgcolor: "transparent",
        width: "100%",
        "& .MuiGrid-container": { width: "100%", margin: 0 },
      }}
    >
      <Typography
        variant="subtitle1"
        fontWeight="bold"
        mb={2}
        fontSize={16}
        sx={{ textAlign: "left", pl: 1 }}
      >
        Recipient Details
      </Typography>
      <Box
        sx={{
          bgcolor: "transparent",
          p: 1,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          "& .MuiFormControl-root": {
            width: "100%",
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            "& .MuiOutlinedInput-root": {
              height: "40px",
              borderRadius: "8px",
              backgroundColor: "#FFFFFF",
              "& fieldset": { borderColor: "none" },
              "&.Mui-focused fieldset": { borderColor: "none" },
              padding: "16px",
              boxSizing: "border-box",
            },
            "& .MuiSelect-select": {
              padding: "16px",
              display: "flex",
              alignItems: "center",
            },
            "& .MuiOutlinedInput-notchedOutline": { border: "none" },
            "& .MuiSelect-icon": { right: "16px" },
          },
        }}
      >
        <RecipientTypeAndName {...props} />
        <LinkedMemberSelect {...props} />
        <TimeZoneAndLanguage {...props} />
      </Box>
    </Box>
  );
}

RecipientDetailsSection.propTypes = {
  formData: PropTypes.object.isRequired,
  renderDropdownButton: PropTypes.func.isRequired,
  RECIPIENT_TYPES: PropTypes.array.isRequired,
  anchorEl: PropTypes.object.isRequired,
  filterButtonRefs: PropTypes.object.isRequired,
  handleCloseSelect: PropTypes.func.isRequired,
  handleTypeToggle: PropTypes.func.isRequired,
  renderRecipientSearchInput: PropTypes.func,
  linkedMembers: PropTypes.array,
  errors: PropTypes.object,
  handleInputChange: PropTypes.func.isRequired,
  TIMEZONE_OPTIONS: PropTypes.array.isRequired,
  sanitizeLanguage: PropTypes.func.isRequired,
};

// Top-level: TaskDetailsSection
function TaskDetailsSection(props) {
  const {
    formData,
    renderDropdownButton,
    getOutreachReasonOptions,
    anchorEl,
    filterButtonRefs,
    handleCloseSelect,
    handleTypeToggle,
    limitDetails,
    errors,
    handleInputChange,
  } = props;

  // Color for details counter: error or over-length -> red, otherwise muted
  let detailsColor = "#5A5875";
  if (errors?.details || (formData.details || "").length > 512) {
    detailsColor = "#d32f2f";
  }

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: "transparent",
        width: "100%",
        "& .MuiGrid-container": { width: "100%", margin: 0 },
      }}
    >
      <Typography
        variant="h6"
        fontWeight="bold"
        mb={1}
        fontSize={16}
        sx={{ textAlign: "left", pl: 1 }}
      >
        Task Details
      </Typography>
      <Box sx={{ bgcolor: "transparent", p: 1, width: "100%" }}>
        <Box
          sx={{ display: "flex", flexWrap: "wrap", gap: "24px", width: "100%" }}
        >
          <Box sx={{ width: { xs: "100%", sm: "calc(50% - 12px)" } }}>
            <Typography
              variant="subtitle2"
              color="#403C6F"
              fontWeight={500}
              fontSize={14}
              gutterBottom
            >
              Due Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DesktopDatePicker
                value={formData.dueDate}
                onChange={(newValue) => handleInputChange("dueDate", newValue)}
                minDate={new Date()}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    slotProps: {
                      input: {
                        sx: {
                          height: "40px",
                          "& input": {
                            padding: "8px 14px",
                            color: formData.dueDate ? "#111827" : "#6B7280",
                            "&::placeholder": {
                              color: "#6B7280",
                              opacity: 1,
                            },
                          },
                        },
                      },
                    },
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#E9E9E8" },
                        "&:hover fieldset": { borderColor: "#E9E9E8" },
                        "&.Mui-focused fieldset": { borderColor: "#E9E9E8" },
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
          <Box sx={{ width: { xs: "100%", sm: "calc(50% - 12px)" } }}>
            <Typography
              variant="subtitle2"
              color="#403C6F"
              fontWeight={500}
              fontSize={14}
              gutterBottom
            >
              Outreach Reason
            </Typography>
            <Box>
              {renderDropdownButton(
                "outreachReason",
                formData.outreachReason,
                getOutreachReasonOptions(),
                "Select Outreach Reason",
                !formData.recipientType,
              )}
              <CommonSelect
                id="outreach-reason-select"
                open={Boolean(anchorEl.outreachReason)}
                anchorEl={anchorEl.outreachReason}
                onClose={() => handleCloseSelect("outreachReason")}
                inQuickview={false}
                filterButtonRef={filterButtonRefs.outreachReason}
                noteTypes={getOutreachReasonOptions().map((opt) => opt.label)}
                selectedTypes={
                  formData.outreachReason
                    ? [
                        getOutreachReasonOptions().find(
                          (t) => t.value === formData.outreachReason,
                        )?.label,
                      ]
                    : []
                }
                handleTypeToggle={(label) => {
                  const sel = getOutreachReasonOptions().find(
                    (t) => t.label === label,
                  );
                  if (sel) handleTypeToggle("outreachReason", sel.value);
                }}
                multiple={false}
                width={
                  filterButtonRefs.outreachReason.current
                    ? `${filterButtonRefs.outreachReason.current.offsetWidth}px`
                    : "auto"
                }
              />
            </Box>
          </Box>
          <Box sx={{ width: "100%", maxWidth: "100%" }}>
            <Typography
              variant="subtitle2"
              color="#403C6F"
              fontWeight={500}
              fontSize={14}
              gutterBottom
            >
              Task Note
            </Typography>
            <Box sx={{ position: "relative" }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="Enter task note here"
                value={formData.details}
                onChange={(e) => {
                  const limited = limitDetails(e.target.value);
                  handleInputChange("details", limited);
                }}
                size="medium"
                error={!!errors?.details}
                helperText={errors?.details || undefined}
                slotProps={{
                  htmlInput: {
                    maxLength: 512,
                  },
                }}
                sx={{
                  backgroundColor: "#FFFFFF",
                  "& .MuiOutlinedInput-root": {
                    "& textarea::placeholder": {
                      color: placeholderText,
                      opacity: 1,
                    },
                    padding: "8px 14px",
                    "& textarea": {
                      minHeight: "100px",
                    },
                    "& fieldset": { borderColor: "#E9E9E8" },
                    "&:hover fieldset": { borderColor: "#E9E9E8" },
                    "&.Mui-focused fieldset": { borderColor: "#E9E9E8" },
                    "&.Mui-disabled": {
                      ...disabledStyles,
                      "& fieldset": { borderColor: "#E9E9E8" },
                    },
                  },
                }}
              />

              <Typography
                variant="caption"
                sx={{
                  position: "absolute",
                  bottom: 6,
                  right: 12,
                  bgcolor: "rgba(255,255,255,0.8)",
                  px: 0.5,
                  borderRadius: "4px",
                  color: detailsColor,
                  pointerEvents: "none",
                }}
              >
                {formData.details.length}/512
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

TaskDetailsSection.propTypes = {
  formData: PropTypes.object.isRequired,
  renderDropdownButton: PropTypes.func.isRequired,
  getOutreachReasonOptions: PropTypes.func.isRequired,
  anchorEl: PropTypes.object.isRequired,
  filterButtonRefs: PropTypes.object.isRequired,
  handleCloseSelect: PropTypes.func.isRequired,
  handleTypeToggle: PropTypes.func.isRequired,
  limitDetails: PropTypes.func,
  errors: PropTypes.object,
  handleInputChange: PropTypes.func,
};

const disabledStyles = {
  backgroundColor: "#EFEFEF !important",
  color: "#999893",
  cursor: "not-allowed",
  "& .MuiOutlinedInput-input": {
    WebkitTextFillColor: "#999893",
    color: "#999893",
  },
  "& .MuiSvgIcon-root": {
    color: "#999893",
  },
};

const RECIPIENT_TYPES = [
  { value: "member", label: "Member" },
  { value: "provider", label: "Provider" },
  { value: "pharmacy", label: "Pharmacy" },
  { value: "others", label: "Others" },
];

export default function TaskCreationModal({
  open,
  onClose,
  onSave,
  task = null,
}) {
  const isEditMode = !!task;

  const [linkedMembers, setLinkedMembers] = useState([]);
  const [errors, setErrors] = useState({
    details: "",
    timeZone: "",
    language: "",
    linkedMember: "",
  });

  const [anchorEl, setAnchorEl] = useState({
    recipientType: null,
    language: null,
    timeZone: null,
    outreachReason: null,
    linkedMember: null,
    recipientName: null,
    program: null,
  });

  const filterButtonRefs = {
    recipientType: useRef(null),
    language: useRef(null),
    timeZone: useRef(null),
    outreachReason: useRef(null),
    linkedMember: useRef(null),
    recipientName: useRef(null),
    program: useRef(null),
  };

  const sanitizeLanguage = (val = "") =>
    val.replaceAll(/[^a-zA-Z\s]/g, "").slice(0, 15);

  const limitDetails = (val = "") =>
    val.length > 512 ? val.slice(0, 512) : val;

  const TIMEZONE_OPTIONS = [
    { label: "MST", value: "MST" },
    { label: "AST", value: "AST" },
    { label: "ChST", value: "ChST" },
    { label: "CST", value: "CST" },
    { label: "AKST", value: "AKST" },
    { label: "HAST", value: "HAST" },
    { label: "SST", value: "SST" },
    { label: "EST", value: "EST" },
    { label: "HST", value: "HST" },
    { label: "PST", value: "PST" },
  ];

  const OUTREACH_REASON_OPTIONS_BY_TYPE = {
    member: [
      { value: "SCHEDULE_CALLBACK", label: "Schedule Callback" },
      { value: "MEMBER_FOLLOW_UP", label: "Member Follow-up" },
    ],
    others: [
      { value: "SCHEDULE_CALLBACK", label: "Schedule Callback" },
      { value: "MEMBER_FOLLOW_UP", label: "Member Follow-up" },
    ],
    pharmacy: [
      { value: "REQUEST_PRESCRIPTION", label: "Request Prescription" },
      { value: "CONFIRM_RX_RECEIVED", label: "Confirm Rx Received" },
      { value: "PHARMACY_CONSULTATION", label: "Pharmacy Consultation" },
    ],
    provider: [
      { value: "PROVIDER_CONSULTATION", label: "Provider consultation" },
      { value: "PROVIDER_EDUCATION", label: "Provider Education" },
      { value: "PROVIDER_FOLLOW_UP", label: "Provider Follow-up" },
      { value: "DOSAGE_OPTIMISATION", label: "Dosing Optimization" },
    ],
  };

  const getOutreachReasonOptions = () => {
    return OUTREACH_REASON_OPTIONS_BY_TYPE[formData.recipientType] ?? [];
  };

  const [filteredRecipients, setFilteredRecipients] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);

  const [formData, setFormData] = useState({
    recipientType: task?.recipientType || "",
    recipientId: task?.recipientId || "",
    recipientName: task?.recipientName || "",
    timeZone: task?.timeZone || "",
    language: sanitizeLanguage(task?.language || ""),
    dueDate: task?.dueDate ? new Date(task.dueDate) : new Date(),
    outreachReason: task?.outreachReason || "",
    details: limitDetails(task?.details || ""),
    linkedMember: task?.linkedMember || "",
    program: task?.program || "",
    lastActivity: task?.lastActivity || "",
  });

  const handleTypeToggle = (field, value) => {
    void handleInputChange(field, value);
    setAnchorEl((prev) => ({ ...prev, [field]: null }));
  };

  const handleOpenSelect = (field, event) => {
    setAnchorEl((prev) => ({ ...prev, [field]: event.currentTarget }));
  };

  const handleCloseSelect = (field) => {
    setAnchorEl((prev) => ({ ...prev, [field]: null }));
  };

  const handleSearchRecipients = async (term) => {
    if (
      !formData.recipientType ||
      !term ||
      term.length < 2 ||
      selectedRecipient
    ) {
      setFilteredRecipients([]);
      setAutocompleteOpen(false);
      return;
    }

    try {
      setSearchLoading(true);
      let searchResults = {};

      switch (formData.recipientType) {
        case "member":
          searchResults = await searchRecipientMembers(term);
          break;
        case "provider":
          searchResults = await searchRecipientProviders(term);
          break;
        case "pharmacy":
          searchResults = await searchRecipientPharmacies(term);
          break;
        default:
          searchResults = { content: [] };
      }

      let content = [];
      if (Array.isArray(searchResults)) {
        content = searchResults;
      } else if (searchResults && Array.isArray(searchResults.content)) {
        content = searchResults.content;
      } else if (Array.isArray(searchResults?.data?.content)) {
        content = searchResults.data.content;
      }

      const transformedResults = content
        .map((item) => ({
          id: item?.id || "",

          displayName: capitalizeFirstLetter(item?.displayName || ""),
          type: item?.type || "",
        }))
        .filter((item) => item.id && item.displayName);

      setFilteredRecipients(transformedResults);

      if (transformedResults.length > 0) {
        setAutocompleteOpen(true);
      } else {
        setAutocompleteOpen(false);
      }
    } catch (error) {
      // Handle the exception: log and set error state
      console.error("Error searching recipients:", error);
      setFilteredRecipients([]);
      setAutocompleteOpen(false);
      setErrors((prev) => ({
        ...prev,
        linkedMember: "Failed to search recipients. Please try again.",
      }));
    } finally {
      setSearchLoading(false);
    }
  };

  const debouncedSearch = useCallback(
    _.debounce((searchTerm) => {
      if (searchTerm && searchTerm.length >= 2) {
        void handleSearchRecipients(searchTerm);
      }
    }, 500),
    [handleSearchRecipients],
  );

  const renderRecipientSearchInput = () => {
    const getNoOptionsText = () => {
      if (!formData.recipientType) return "Select recipient type first";
      if (searchInput.length < 2) return "Type at least 2 characters to search";
      return "No recipients found";
    };

    return (
      <Box sx={{ position: "relative" }}>
        <Autocomplete
          fullWidth
          freeSolo
          open={
            autocompleteOpen &&
            !!formData.recipientType &&
            filteredRecipients.length > 0 &&
            searchInput.trim().length >= 2
          }
          onOpen={() => {
            if (
              filteredRecipients.length > 0 &&
              formData.recipientType &&
              searchInput.trim().length >= 2
            ) {
              setAutocompleteOpen(true);
            }
          }}
          onClose={() => setAutocompleteOpen(false)}
          options={filteredRecipients}
          getOptionLabel={(option) => {
            if (typeof option === "string") return option;
            return option?.displayName || "";
          }}
          filterOptions={(options) => options}
          value={selectedRecipient}
          inputValue={searchInput}
          onInputChange={(event, newInputValue) => {
            if (!formData.recipientType) {
              return;
            }

            if (newInputValue.length > 150) return;

            setSearchInput(newInputValue);

            if (
              selectedRecipient &&
              newInputValue !== selectedRecipient.displayName
            ) {
              setSelectedRecipient(null);
              setFormData((prev) => ({
                ...prev,
                recipientId: "",
                recipientName: newInputValue,
              }));
            }

            if (newInputValue.trim() === "") {
              debouncedSearch.cancel();

              setFilteredRecipients([]);
              setSelectedRecipient(null);
              setAutocompleteOpen(false);
              setSearchLoading(false);
              setFormData((prev) => ({
                ...prev,
                recipientId: "",
                recipientName: "",
              }));
            } else if (newInputValue.trim().length >= 2) {
              debouncedSearch(newInputValue.trim());
            } else {
              debouncedSearch.cancel();
              setFilteredRecipients([]);
              setAutocompleteOpen(false);
              setSearchLoading(false);
            }
          }}
          onChange={(event, newValue) => {
            if (
              newValue &&
              typeof newValue === "object" &&
              newValue.id !== "placeholder" &&
              formData.recipientType
            ) {
              void handleRecipientSelect(newValue.displayName);
            }
          }}
          disabled={!formData.recipientType}
          loading={searchLoading && !!formData.recipientType}
          noOptionsText={getNoOptionsText()}
          forcePopupIcon={false}
          autoHighlight={false}
          clearIcon={null}
          slotProps={{
            listbox: {
              "aria-label": "Recipient search results",
            },
            paper: {
              style: {
                marginTop: "4px",
              },
            },
            popper: {
              sx: {
                "& .MuiPaper-root": {
                  backgroundColor: "#6E6D92",
                  color: "white",
                  marginTop: "4px",
                  borderRadius: "8px",
                  overflow: "hidden",
                  boxShadow:
                    "0px 4px 6px -1px rgba(0,0,0,0.1), 0px 2px 4px -1px rgba(0,0,0,0.06)",
                },
                "& .MuiAutocomplete-listbox": {
                  padding: "4px 0",
                  "& li": {
                    padding: "4px 16px",
                    minHeight: "32px",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.08)",
                    },
                    '&[aria-selected="true"]': {
                      backgroundColor: "rgba(255, 255, 255, 0.15)",
                    },
                    "& .MuiListItemText-root": {
                      margin: 0,
                    },
                  },
                },
                "& .MuiAutocomplete-noOptions": {
                  padding: "8px 16px",
                  color: "rgba(255, 255, 255, 0.7)",
                },
              },
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, 4],
                  },
                },
              ],
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              id="recipient-search-input"
              placeholder={
                formData.recipientType
                  ? "Search recipient..."
                  : "Select recipient type first"
              }
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <RecipientEndAdornment
                      searchLoading={searchLoading}
                      recipientType={formData.recipientType}
                      selectedRecipient={selectedRecipient}
                      onClear={clearRecipientSelection}
                      onTrigger={triggerRecipientSearch}
                    />
                  ),
                },
                htmlInput: {
                  ...params.inputProps,
                  maxLength: 150,
                  "aria-label": "Search recipient",
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: formData.recipientType
                    ? "#FFFFFF"
                    : "#EEEEEE",
                  border: "1px solid #E9E9E8",
                  height: "40px !important",
                  "& fieldset": { borderColor: "#E9E9E8" },
                  "&:hover fieldset": {
                    borderColor: formData.recipientType ? "#403C6F" : "#E9E9E8",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: formData.recipientType ? "#403C6F" : "#E9E9E8",
                  },
                  "&.Mui-disabled": {
                    ...disabledStyles,
                    "& fieldset": {
                      borderColor: "#E9E9E8 !important",
                    },
                  },
                  paddingRight: "20px !important",
                },
                "& input": {
                  padding: "8px 60px 8px 14px !important",
                  color: formData.recipientType ? "inherit" : "#999999",
                  cursor: formData.recipientType ? "text" : "not-allowed",
                },
                "& input::placeholder": {
                  color: formData.recipientType ? "#999893" : "#CCCCCC",
                  opacity: 1,
                },
              }}
            />
          )}
          renderOption={(props, option, { selected }) => (
            <Box
              component="li"
              {...props}
              key={option.id}
              onClick={() => handleOptionSelect(option)}
              sx={{
                display: "flex",
                alignItems: "center",
                fontSize: "14px",
                padding: "8px 16px",
                margin: "2px 8px",
                backgroundColor: selected ? "#403c6f" : "transparent",
                color: "white",
                borderRadius: "4px",
                cursor: "pointer",
                minHeight: "40px",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
                ...(option.id === "placeholder" && {
                  color: "rgba(255, 255, 255, 0.7)",
                  fontStyle: "italic",
                  pointerEvents: "none",
                }),
              }}
            >
              <Box
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  textShadow: "0 0 1px rgba(0, 0, 0, 0.3)",
                }}
              >
                {option.displayName}
              </Box>
            </Box>
          )}
        />
        {formData.recipientType && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
            <Typography
              variant="caption"
              sx={{ fontSize: "12px", color: "#5A5875" }}
            >
              {searchInput?.length || 0}/150
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const renderDropdownButton = (
    field,
    value,
    options,
    placeholder,
    disabled = false,
  ) => {
    const selectedLabel =
      options.find((opt) => opt.value === value)?.label || "";
    let buttonColor;
    if (disabled) {
      buttonColor = "#9CA3AF";
    } else if (value) {
      buttonColor = "#000000";
    } else {
      buttonColor = "#999893";
    }
    let spanFontWeight = value ? "normal" : "400";
    let spanColor = value ? "inherit" : "#999893";
    return (
      <Button
        ref={filterButtonRefs[field]}
        onClick={(e) => {
          if (disabled) return;
          handleOpenSelect(field, e);
        }}
        disabled={disabled}
        sx={{
          width: "100%",
          minWidth: "200px",
          height: "40px",
          border: "1px solid #E9E9E8",
          borderRadius: "8px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 12px",
          backgroundColor: disabled ? "#F3F4F6" : "#FFFFFF",
          textTransform: "none",
          color: buttonColor,
          fontSize: "14px",
          cursor: disabled ? "not-allowed" : "pointer",
          pointerEvents: disabled ? "none" : "auto",
          "&:hover": {
            backgroundColor: disabled ? "#F3F4F6" : "#FFFFFF",
          },
          "&:focus-visible": {
            outline: "2px solid #403C6F",
            outlineOffset: "2px",
          },
        }}
      >
        <span
          style={{
            fontWeight: spanFontWeight,
            color: spanColor,
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {value ? selectedLabel : placeholder}
        </span>
        <DropdownIcon style={{ marginLeft: "8px" }} />
      </Button>
    );
  };

  // Reduce nesting inside Autocomplete's renderInput by extracting helpers
  const clearRecipientSelection = () => {
    debouncedSearch.cancel();
    setSelectedRecipient(null);
    setSearchInput("");
    setFilteredRecipients([]);
    setAutocompleteOpen(false);
    setSearchLoading(false);
    setFormData((prev) => ({
      ...prev,
      recipientId: "",
      recipientName: "",
      timeZone: "",
      language: "",
      recentActivityStatus: "",
      recentActivityAt: "",
      program: "",
    }));

    setLinkedMembers([]);
  };

  const triggerRecipientSearch = () => {
    if (formData.recipientType) {
      const trimmedInput = (searchInput || "").trim();
      if (trimmedInput.length >= 2) {
        void handleSearchRecipients(trimmedInput);
      }
    }
  };

  // Helpers to reduce cognitive complexity in handleRecipientSelect
  const extractContent = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.content)) return data.content;
    if (Array.isArray(data.data?.content)) return data.data.content;
    return [];
  };

  const transformMembers = (content) =>
    content
      .map((member) => ({
        id: member.id || member.memberId,
        name: member.name || member.displayName || member.memberName,
      }))
      .filter((member) => member.id && member.name);

  const fetchRecipientDetails = async (type, id) => {
    if (type === "pharmacy") return fetchPharmacyDetails(id);
    if (type === "provider") return fetchProviderDetails(id);
    return null;
  };

  const applyRecentActivity = (form, detailsResp) => {
    if (detailsResp?.outcome && detailsResp?.timestamp) {
      return {
        ...form,
        recentActivityStatus: detailsResp.outcome,
        recentActivityAt: detailsResp.timestamp,
        recentActivityStatusColor: detailsResp.colourCode || "",
      };
    }
    return form;
  };

  const handleRecipientSelect = async (recipientName) => {
    const selectedOption = filteredRecipients.find(
      (recipient) => recipient.displayName === recipientName,
    );

    if (!selectedOption || selectedOption.id === "placeholder") return;

    setSelectedRecipient(selectedOption);
    setSearchInput(selectedOption.displayName);

    let baseFormData = {
      ...formData,
      recipientId: selectedOption.id,
      recipientName: selectedOption.displayName,
      recentActivityStatus: "",
      recentActivityAt: "",
    };

    const type = formData.recipientType;

    // Provider/Pharmacy: fetch linked members and recent details
    if (type === "provider" || type === "pharmacy") {
      try {
        const linkedMembersData = await fetchLinkedMembers(
          type.toUpperCase(),
          selectedOption.id,
        );
        const members = transformMembers(extractContent(linkedMembersData));
        setLinkedMembers(members);

        const detailsResp = await fetchRecipientDetails(
          type,
          selectedOption.id,
        );
        baseFormData = applyRecentActivity(baseFormData, detailsResp);
      } catch (error) {
        // Handle the exception: log and set error state
        console.error("Error fetching linked members or details:", error);
        setErrors((prev) => ({
          ...prev,
          linkedMember:
            "Failed to fetch linked members or details. Please try again.",
        }));
      }
    }

    // Member: fetch programs and update form data directly
    if (type === "member") {
      const programsData = await fetchProgramsForRecipient(
        selectedOption.id,
        type,
      );
      if (programsData && typeof programsData === "object") {
        setFormData((prev) => ({
          ...prev,
          timeZone: programsData.timezone || "",
          language: programsData.language || "",
          recentActivityStatus:
            programsData.recentActivity?.outcome ||
            programsData.recentActivity?.status ||
            "",
          recentActivityAt: programsData.recentActivity?.timestamp || "",
          recentActivityStatusColor:
            programsData.recentActivity?.colourCode || "",
          recipientId: selectedOption.id,
          recipientName: selectedOption.displayName,
        }));
        return;
      }
    }

    setFormData(baseFormData);
  };

  const fetchProgramsForRecipient = async (recipientId, recipientType) => {
    try {
      const programsData = await fetchRecipientPrograms(
        recipientId,
        recipientType,
      );

      return programsData;
    } catch (error) {
      // Handle the exception: log and set error state
      console.error("Error fetching programs for recipient:", error);

      setErrors((prev) => ({
        ...prev,
        linkedMember:
          "Failed to fetch programs for the selected member. Please try again.",
      }));
      return null;
    }
  };

  const handleOptionSelect = (option) => {
    void handleRecipientSelect(option.displayName);
    setAutocompleteOpen(false);
  };

  const handleInputChange = async (field, value) => {
    const newFormData = {
      ...formData,
      [field]: value,
    };
    if (field === "details") {
      const trimmed = (value || "").trim();
      setErrors((prev) => ({
        ...prev,
        details: trimmed ? "" : "Details are required",
      }));
    }

    if (field === "timeZone") {
      setErrors((prev) => ({ ...prev, timeZone: "" }));
    }

    if (field === "language") {
      setErrors((prev) => ({ ...prev, language: "" }));
    }

    if (field === "linkedMember") {
      setErrors((prev) => ({
        ...prev,
        linkedMember:
          (formData.recipientType === "provider" ||
            formData.recipientType === "pharmacy") &&
          !value
            ? "Linked Member is required"
            : "",
      }));
    }

    if (field === "recipientType") {
      newFormData.recipientId = "";
      newFormData.recipientName = "";
      newFormData.timeZone = "";
      newFormData.language = "";
      newFormData.recentActivityStatus = "";
      newFormData.recentActivityAt = "";
      newFormData.linkedMember = "";
      newFormData.program = "";
      newFormData.outreachReason = "";

      setErrors((prev) => ({
        ...prev,
        timeZone: "",
        language: "",
        linkedMember: "",
      }));

      setSelectedRecipient(null);
      setFilteredRecipients([]);
      setSearchInput("");
      setAutocompleteOpen(false);
    }

    if (
      field === "linkedMember" &&
      (formData.recipientType === "provider" ||
        formData.recipientType === "pharmacy")
    ) {
      const data = await fetchProgramsForRecipient(
        value,
        formData.recipientType,
      );
      if (data && typeof data === "object") {
        setFormData((prev) => ({
          ...prev,
          linkedMember: value,
          timeZone: data.timezone || "",
          language: data.language || "",
        }));
        return;
      }
    }

    setFormData(newFormData);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const trimmedDetails = (formData.details || "").trim();
    let localErrors = { ...errors };
    if (!trimmedDetails) {
      localErrors.details = "Details are required";
    }

    if (
      (formData.recipientType === "provider" ||
        formData.recipientType === "pharmacy") &&
      !formData.linkedMember
    ) {
      localErrors.linkedMember = "Linked Member is required";
    }
    if (!formData.recipientType || !formData.outreachReason) {
      setErrors(localErrors);
      return;
    }
    if (
      localErrors.details ||
      localErrors.timeZone ||
      localErrors.language ||
      localErrors.linkedMember
    ) {
      setErrors(localErrors);
      return;
    }

    const newTask = {
      ...formData,
      id: Date.now().toString(),
      status: "pending",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    let payload = { ...newTask };
    if (formData.recipientType === "others") {
      payload.recipientName = searchInput;
      delete payload.recipientId;
    }
    payload.note = trimmedDetails;
    if (onSave) {
      onSave(payload);
    }

    setFormData({
      recipientType: "",
      recipientId: "",
      recipientName: "",
      timeZone: "",
      language: "",
      dueDate: new Date(),
      outreachReason: "",
      details: "",
      linkedMember: "",
      program: "",
      recentActivityStatus: "",
      recentActivityAt: "",
    });
    setErrors({
      details: "",
      timeZone: "",
      language: "",
      linkedMember: "",
    });
    setErrors({
      details: "",
      timeZone: "",
      language: "",
      linkedMember: "",
    });

    setSelectedRecipient(null);
    setFilteredRecipients([]);
    setSearchInput("");
    setAutocompleteOpen(false);

    setLinkedMembers([]);

    onClose();
  };

  const handleCancel = () => {
    setFormData({
      recipientType: "",
      recipientId: "",
      recipientName: "",
      timeZone: "",
      language: "",
      dueDate: new Date(),
      outreachReason: "",
      details: "",
      linkedMember: "",
      program: "",
      recentActivityStatus: "",
      recentActivityAt: "",
    });
    setErrors({ details: "", timeZone: "", language: "", linkedMember: "" });
    setSelectedRecipient(null);
    setFilteredRecipients([]);
    setSearchInput("");
    setAutocompleteOpen(false);

    setLinkedMembers([]);

    onClose();
  };

  useEffect(() => {
    if (searchInput.trim() === "") {
      setFilteredRecipients([]);
      setAutocompleteOpen(false);
    }
  }, [searchInput]);

  // RecipientTypeAndName moved to top-level

  // LinkedMemberSelect moved to top-level

  // TimeZoneAndLanguage moved to top-level

  // RecipientDetailsSection moved to top-level

  // Recent activity display moved to top-level RecentActivityDisplay

  // TaskDetailsSection moved to top-level

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="new-task-dialog-title"
      slotProps={{
        root: {
          keepMounted: false,
          disableEnforceFocus: false,
          disableAutoFocus: false,
          disableRestoreFocus: false,
        },
        paper: {
          sx: {
            borderRadius: 2,
            bgcolor: "#F7F8FB",
            maxWidth: "700px",
            width: "calc(100% - 80px)",
            border: "1px solid #E9E9E8",
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.05)",
            "& .MuiDialogContent-root": {
              padding: 0,
              overflowY: "auto",
              maxHeight: "calc(90vh - 120px)",
            },
          },
        },
      }}
    >
      <DialogTitle
        id="new-task-dialog-title"
        sx={{
          fontWeight: "bold",
          fontSize: "20px",
          py: 3,
          px: 3,
          m: 0,
          pb: 1,
          textAlign: "left",
          pl: 3,
        }}
      >
        {isEditMode ? "Edit Task" : "Task Creation"}
        <IconButton
          aria-label={
            isEditMode ? "Close edit task dialog" : "Close new task dialog"
          }
          onClick={handleCancel}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, px: 1, pb: 1 }}>
        <Box
          sx={{
            px: 1,
            pb: 1,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box
            sx={{
              border: "1px solid #E9E9E8",
              borderRadius: 2,
              overflow: "hidden",
              mb: 1,
              bgcolor: "#FFFFFF",
            }}
          >
            <RecipientDetailsSection
              formData={formData}
              renderDropdownButton={renderDropdownButton}
              RECIPIENT_TYPES={RECIPIENT_TYPES}
              anchorEl={anchorEl}
              filterButtonRefs={filterButtonRefs}
              handleCloseSelect={handleCloseSelect}
              handleTypeToggle={handleTypeToggle}
              renderRecipientSearchInput={renderRecipientSearchInput}
              linkedMembers={linkedMembers}
              errors={errors}
              handleInputChange={handleInputChange}
              TIMEZONE_OPTIONS={TIMEZONE_OPTIONS}
              sanitizeLanguage={sanitizeLanguage}
            />
            <RecentActivityDisplay
              recentActivityStatus={formData.recentActivityStatus}
              recentActivityAt={formData.recentActivityAt}
              recentActivityStatusColor={formData.recentActivityStatusColor}
            />
            <TaskDetailsSection
              formData={formData}
              renderDropdownButton={renderDropdownButton}
              getOutreachReasonOptions={getOutreachReasonOptions}
              anchorEl={anchorEl}
              filterButtonRefs={filterButtonRefs}
              handleCloseSelect={handleCloseSelect}
              handleTypeToggle={handleTypeToggle}
              limitDetails={limitDetails}
              errors={errors}
              handleInputChange={handleInputChange}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "flex-end",
          gap: "10px",
          p: 2,
          position: "relative",
          zIndex: 0,
        }}
      >
        <Button
          onClick={handleCancel}
          variant="outlined"
          sx={{
            height: 38,
            gap: "10px",
            opacity: 1,
            borderRadius: "52px",
            border: "1px solid #9F9DB6",
            color: "#403C6F",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(64, 60, 111, 0.04)",
              borderColor: "#6E6B7B",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            !formData.recipientType ||
            !formData.outreachReason ||
            (formData.recipientType !== "others" && !formData.recipientId) ||
            !(formData.details || "").trim() ||
            !!errors.details
          }
          sx={{
            height: 38,
            gap: "10px",
            opacity: 1,
            borderRadius: "52px",
            backgroundColor: "#3F3D56",
            color: "#FFFFFF",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#2F2C46",
            },
            "&.Mui-disabled": {
              backgroundColor: "#E9E9E8",
              color: "#999893",
            },
          }}
        >
          {isEditMode ? "Update Task" : "Create Task"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

TaskCreationModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  task: PropTypes.object,
};

const spinKeyframes = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

if (typeof document !== "undefined") {
  const style = document.createElement("style");
  style.textContent = spinKeyframes;
  document.head.appendChild(style);
}
